// 右邊「Cue 庫」分頁：專案所有 Cue 庫的 cue，搜尋 / 篩選 / 預覽 / 改名稱與編號 / 誰在用 / 刪除。規格 5.2、第 10 節。
// 只在 Cue 庫模式的專案出現。點一列 = 在 3D 預覽這個 cue 的畫面，不改任何歌。

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Check, Copy, Hash, Library, ListMusic, Pencil, Plus, RotateCcw, Search, Timer, Trash2, X } from 'lucide-react';
import Icon from './Icon';
import OverflowMenu from './OverflowMenu';
import type { LibraryCue, LibraryCueUsage } from '../lib/cueLibraryApi';
import { filterLibraryCues, usageLabel, usageSlotText, type LibraryFilter } from '../lib/cueLibraryList';
import { labelError, LABEL_MAX } from '../lib/lookDraft';
import { crossfadeLabel, parseCrossfadeInput } from '../lib/cueTiming';
import './CueLibraryTab.css';

interface Props {
  cues: LibraryCue[];
  loading: boolean;
  /** 舞台上現在看的 Cue 庫的 cue */
  shownId: string | null;
  /** 現在是不是「只預覽」（這首歌沒有用到它） */
  previewing: boolean;
  /** 點一列：預覽 */
  onSelect: (id: string) => void;
  onCopy: (lc: LibraryCue) => void;
  /** 回 null = 改好了；回文字 = 錯誤 */
  onRename: (lc: LibraryCue, name: string) => Promise<string | null>;
  onRelabel: (lc: LibraryCue, label: string) => Promise<string | null>;
  /** 改這個 cue 預設的轉場秒數（沒另外設的歌會跟著變） */
  onSetFade: (lc: LibraryCue, seconds: number) => Promise<string | null>;
  onDelete: (lc: LibraryCue) => void;
  onResetAll: (lc: LibraryCue) => void;
  /** 點誰在用的連結：跳去那首歌的那個 cue */
  onJump: (songId: string, cueId: string) => void;
  /** 歌單上這首歌現在的代號和名字（用來顯示「用在：S01 …」；找不到 = 用後台給的） */
  songInfo?: (songId: string) => { code?: string | null; name: string } | null;
  /** 加到目前選的這首歌（沒選歌 = 不給） */
  onAddToSong?: (lc: LibraryCue) => void;
  /** 底下的「另存成新的 cue」（從目前舞台） */
  onSaveAs: () => void;
  busy: boolean;
  /** 「正在編輯」底下的小分頁：清單 / 機關 */
  subTab?: 'list' | 'mech';
  onSubTab?: (t: 'list' | 'mech') => void;
  /** 「機關」小分頁的內容（跟右邊「機關」分頁同一個元件） */
  mechanisms?: ReactNode;
}

function UsageLinks({ usage, onJump, songInfo }: { usage: LibraryCueUsage; onJump: Props['onJump']; songInfo?: Props['songInfo'] }) {
  if (usage.slots.length === 0) return <span className="cue-lib__nouse">沒有歌在用</span>;
  return (
    <>
      {usage.slots.map((s, i) => {
        // 歌的代號 / 名字以歌單現在的為準（在歌單上改了代號，Cue 庫讀到的舊資料還沒更新）
        const name = songInfo?.(s.songId)?.name ?? s.songName;
        return (
        <span key={s.cueId}>
          {i > 0 && '、'}
          <button type="button" className="cue-lib__link" onClick={() => onJump(s.songId, s.cueId)} title={`跳到 ${name} 的這個 cue`}>
            {usageSlotText(s, songInfo)}
          </button>
        </span>
        );
      })}
    </>
  );
}

export default function CueLibraryTab({ cues, loading, shownId, previewing, onSelect, onCopy, onRename, onRelabel, onSetFade, onDelete, onResetAll, onJump, onAddToSong, onSaveAs, busy, songInfo, subTab = 'list', onSubTab, mechanisms }: Props) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<LibraryFilter>('all');
  const [editing, setEditing] = useState<{ id: string; field: 'name' | 'label' | 'fade' } | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const shown = useMemo(() => filterLibraryCues(cues, query, filter), [cues, query, filter]);
  const current = cues.find(c => c.id === shownId) ?? null;

  useEffect(() => { if (editing) setTimeout(() => inputRef.current?.select(), 20); }, [editing]);
  // 正在改的那個 cue 不見了（被刪 / 重新讀）→ 收起來
  useEffect(() => { if (editing && !cues.some(c => c.id === editing.id)) setEditing(null); }, [cues, editing]);
  // 目前選的在清單裡捲到看得見（J / K 切換時）
  useEffect(() => {
    if (!shownId) return;
    document.querySelector<HTMLElement>(`[data-libcue="${CSS.escape(shownId)}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [shownId]);

  function startEdit(lc: LibraryCue, field: 'name' | 'label' | 'fade') {
    setEditing({ id: lc.id, field });
    setDraft(field === 'name' ? lc.name : field === 'fade' ? String(lc.crossfadeSeconds) : lc.label);
    setError(null);
  }
  const editingCue = editing ? cues.find(c => c.id === editing.id) ?? null : null;
  const localError = editing && editingCue
    ? (editing.field === 'label'
      ? labelError(draft, cues.filter(c => c.id !== editingCue.id).map(c => c.label))
      : editing.field === 'fade'
        ? (parseCrossfadeInput(draft) === null ? '轉場秒數要填 0 或正的數字' : null)
        : (draft.trim() ? null : '名稱不能空白'))
    : null;

  async function commit() {
    if (!editing || !editingCue || saving) return;
    if (localError) { setError(localError); return; }
    const value = draft.trim();
    if (editing.field === 'fade') {
      const sec = parseCrossfadeInput(draft) as number;
      if (sec === editingCue.crossfadeSeconds) { setEditing(null); return; }
      setSaving(true);
      const e = await onSetFade(editingCue, sec);
      setSaving(false);
      if (e) setError(e); else setEditing(null);
      return;
    }
    if (value === (editing.field === 'name' ? editingCue.name : editingCue.label)) { setEditing(null); return; }
    setSaving(true);
    const err = await (editing.field === 'name' ? onRename(editingCue, value) : onRelabel(editingCue, value));
    setSaving(false);
    if (err) setError(err); else setEditing(null);
  }

  return (
    <div className="cue-lib">
      <div className="cue-lib__tools">
        <label className="cue-lib__search">
          <Icon icon={Search} />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="找 cue（編號或名稱）"
            aria-label="找 cue（編號或名稱）"
          />
        </label>
        <div className="cue-lib__chips" role="group" aria-label="篩選">
          {([['all', '全部'], ['unused', '沒有歌在用']] as const).map(([k, l]) => (
            <button key={k} type="button" className={'cue-lib__chip' + (filter === k ? ' is-active' : '')} aria-pressed={filter === k} onClick={() => setFilter(k)}>{l}</button>
          ))}
        </div>
      </div>

      {current && (
        <div className="cue-lib__now" role="status">
          <strong>Cue {current.label}「{current.name}」</strong>
          <span className="cue-lib__nowsub">
            用在：<UsageLinks usage={current.usage} onJump={onJump} songInfo={songInfo} />
          </span>
          {previewing && <span className="cue-lib__nowsub">這首歌沒有用到它。在 3D 上微調會變成「還沒存的改動」，按上面的「覆蓋」只會改 Cue 庫，不動這首歌。</span>}
        </div>
      )}

      {current && onSubTab && (
        <div className="cue-lib__subtabs" role="tablist" aria-label="Cue 庫的內容">
          {([['list', 'Cue 清單'], ['mech', '機關']] as const).map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={subTab === k} className={'cue-lib__subtab' + (subTab === k ? ' is-active' : '')} onClick={() => onSubTab(k)}>{l}</button>
          ))}
        </div>
      )}
      {current && onSubTab && subTab === 'mech' ? (
        <div className="cue-lib__mech">{mechanisms}</div>
      ) : (<>
      <div className="cue-lib__list" role="listbox" aria-label="Cue 庫的 cue">
        {loading && cues.length === 0 ? (
          <div className="editor-empty muted">載入中…</div>
        ) : cues.length === 0 ? (
          <div className="editor-empty">
            <Icon icon={Library} size="xl" className="editor-empty__icon" />
            <div>Cue 庫還是空的</div>
            <small className="muted">先把舞台擺好，按下面的「另存成新的 cue」。</small>
          </div>
        ) : shown.length === 0 ? (
          <div className="editor-empty muted">沒有符合的 cue</div>
        ) : shown.map(lc => {
          const isEditing = editing?.id === lc.id;
          const used = lc.usage.slotCount > 0;
          return (
            <div
              key={lc.id}
              data-libcue={lc.id}
              role="option"
              aria-selected={lc.id === shownId}
              className={'cue-lib__row' + (lc.id === shownId ? ' is-selected' : '')}
              onClick={() => { if (!isEditing) onSelect(lc.id); }}
            >
              {isEditing && editing.field === 'label' ? (
                <input
                  ref={inputRef}
                  className="cue-lib__edit cue-lib__edit--label"
                  value={draft}
                  maxLength={LABEL_MAX + 8}
                  aria-label="編號"
                  aria-invalid={!!localError}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => { setDraft(e.target.value); setError(null); }}
                  onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') void commit(); else if (e.key === 'Escape') setEditing(null); }}
                />
              ) : (
                <span className="cue-lib__badge" title="編號">{lc.label}</span>
              )}
              <div className="cue-lib__main">
                {isEditing && editing.field === 'name' ? (
                  <input
                    ref={inputRef}
                    className="cue-lib__edit"
                    value={draft}
                    aria-label="名稱"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => { setDraft(e.target.value); setError(null); }}
                    onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') void commit(); else if (e.key === 'Escape') setEditing(null); }}
                  />
                ) : (
                  <span className="cue-lib__name">{lc.name}</span>
                )}
                <span className={'cue-lib__use' + (used ? '' : ' is-unused')}>
                  {usageLabel(lc.usage)}
                  {' · '}
                  {isEditing && editing.field === 'fade' ? (
                    <span className="cue-lib__fadeedit" onClick={(e) => e.stopPropagation()}>
                      預設轉場
                      <input
                        ref={inputRef}
                        className="cue-lib__edit cue-lib__edit--fade"
                        value={draft}
                        inputMode="decimal"
                        aria-label="預設轉場秒數"
                        aria-invalid={!!localError}
                        onChange={(e) => { setDraft(e.target.value); setError(null); }}
                        onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') void commit(); else if (e.key === 'Escape') setEditing(null); }}
                      />
                      秒（0 = 直接切換；沒另外設的歌會跟著變）
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="cue-lib__link cue-lib__fade"
                      title="這個 cue 預設的轉場；點一下改。歌裡可以另外設自己的"
                      onClick={(e) => { e.stopPropagation(); startEdit(lc, 'fade'); }}
                      disabled={busy}
                    >{crossfadeLabel(lc.crossfadeSeconds)}</button>
                  )}
                </span>
                {isEditing && (
                  <span className="cue-lib__editbar" onClick={(e) => e.stopPropagation()}>
                    {(error || localError) && <span className="cue-lib__error" role="alert">{error || localError}</span>}
                    <button type="button" className="btn btn--sm btn--primary" onClick={() => void commit()} disabled={saving || !!localError}><Icon icon={Check} /> 存</button>
                    <button type="button" className="btn btn--sm btn--ghost" onClick={() => setEditing(null)} disabled={saving}><Icon icon={X} /> 取消</button>
                  </span>
                )}
              </div>
              <span onClick={(e) => e.stopPropagation()}>
                <OverflowMenu
                  label={`Cue ${lc.label} 的更多動作`}
                  align="auto"
                  items={[
                    ...(onAddToSong ? [{ key: 'addsong', label: '加到這首歌', icon: Plus, onSelect: () => onAddToSong(lc), disabled: busy }] : []),
                    { key: 'copy', label: '另存 / 複製成新的 cue', icon: Copy, onSelect: () => onCopy(lc) },
                    { key: 'rename', label: '改名稱', icon: Pencil, onSelect: () => startEdit(lc, 'name') },
                    { key: 'relabel', label: '改編號', icon: Hash, onSelect: () => startEdit(lc, 'label') },
                    { key: 'fade', label: '改預設轉場', icon: Timer, onSelect: () => startEdit(lc, 'fade') },
                    { key: 'usage', label: '哪幾首歌在用', icon: ListMusic, onSelect: () => onSelect(lc.id) },
                    { key: 'reset', label: '這個 cue 全部回到預設', icon: RotateCcw, onSelect: () => onResetAll(lc), disabled: busy, separatorBefore: true },
                    { key: 'delete', label: used ? '刪除（還有歌在用）' : '刪除', icon: Trash2, danger: true, onSelect: () => onDelete(lc), disabled: busy },
                  ]}
                />
              </span>
            </div>
          );
        })}
      </div>

      <div className="cue-lib__foot">
        <button type="button" className="btn btn--sm btn--ghost" onClick={onSaveAs} disabled={busy}><Icon icon={Copy} /> 另存成新的 cue</button>
        <span className="cue-lib__hint">J / K 照編號上下切換</span>
      </div>
      </>)}
    </div>
  );
}
